<template>
  <div class="alert-thresholds-inline fba-thresholds-inline">
    <div class="fba-inline-title">
      <i class="cog icon"></i>
      <span>Alert Thresholds — {{ productTitle }} · {{ marketplaceLabel }}</span>
    </div>

    <div class="fba-inline-fields">
      <div class="fba-inline-field">
        <label>Warning (units)</label>
        <input
          v-model.number="form.amberThreshold"
          type="number"
          min="-1"
          placeholder="—"
          :disabled="!resolvedProductId"
          :class="['fba-inline-input', inputFilledClass(form.amberThreshold)]"
        />
        <span class="fba-field-hint">units left</span>
      </div>
      <div class="fba-inline-field">
        <label>Alert (units)</label>
        <input
          v-model.number="form.redThreshold"
          type="number"
          min="-1"
          placeholder="—"
          :disabled="!resolvedProductId"
          :class="['fba-inline-input', inputFilledClass(form.redThreshold)]"
        />
        <span class="fba-field-hint">units left</span>
      </div>
      <div class="fba-inline-divider" />
      <div class="fba-inline-field">
        <label>Warning (days)</label>
        <input
          v-model.number="form.amberDays"
          type="number"
          min="-1"
          placeholder="—"
          :disabled="!resolvedProductId"
          :class="['fba-inline-input', inputFilledClass(form.amberDays)]"
        />
        <span class="fba-field-hint">days of stock</span>
      </div>
      <div class="fba-inline-field">
        <label>Alert (days)</label>
        <input
          v-model.number="form.redDays"
          type="number"
          min="-1"
          placeholder="—"
          :disabled="!resolvedProductId"
          :class="['fba-inline-input', inputFilledClass(form.redDays)]"
        />
        <span class="fba-field-hint">days of stock</span>
      </div>
      <div class="fba-inline-divider" />
      <div class="fba-inline-field">
        <label>Avg interval</label>
        <input
          v-model.number="form.velocityLookbackDays"
          type="number"
          min="1"
          placeholder="14"
          :disabled="!resolvedProductId"
          :class="['fba-inline-input', inputFilledClass(form.velocityLookbackDays)]"
        />
        <span class="fba-field-hint">days for avg</span>
      </div>
    </div>

    <div class="fba-inline-actions">
      <button
        type="button"
        class="fba-btn-save"
        :disabled="saving || !resolvedProductId"
        @click="save"
      >
        Save
      </button>
      <button type="button" class="fba-btn-cancel" @click="cancel">Cancel</button>
      <span class="fba-inline-hint">Leave blank to use account defaults.</span>
    </div>

    <div v-if="saveError" class="alert-thresholds-error">{{ saveError }}</div>
    <div v-if="saving" class="alert-thresholds-loading">
      <i class="notched circle loading icon"></i> Saving…
    </div>
  </div>
</template>

<script>
import {
  unwrapApiNumber,
  listingIdFromProduct,
  extractThresholdGetItems,
  productIdFromThresholdRow,
  formFieldsFromFbaSettings,
  httpResponseEntity
} from '../../utils/stockAlertThresholdsApiParse'

export default {
  name: 'AlertThresholdsInline',
  props: {
    item: { type: Object, default: null },
    /** Resolved listing id (same as Stock page / stock-alert-thresholds API). */
    productId: { type: [String, Number], default: null },
    /** When Issues navbar has exactly one seller account selected, scope PATCH. */
    sellerAccountId: { type: String, default: null },
    productTitle: { type: String, default: '' },
    marketplace: { type: String, default: '' },
    /** FBA: silent GET on open + PATCH on Save. Watchdog `item.settings` often carries account defaults (e.g. 7/3/7 days), not listing overrides. */
    stockThresholdsApiBase: { type: String, default: '/api/v1/stock-alert-thresholds' },
    /** @deprecated Retained for compatibility; FBA thresholds use stockThresholdsApiBase. */
    apiBaseUrl: { type: String, default: '/api/v1/watchdogs' }
  },
  data: function () {
    return {
      form: {
        amberThreshold: null,
        redThreshold: null,
        amberDays: null,
        redDays: null,
        velocityLookbackDays: null
      },
      saving: false,
      saveError: null
    }
  },
  computed: {
    marketplaceLabel: function () {
      if (!this.marketplace) return ''
      const m = this.marketplace.toUpperCase()
      const map = { US: 'US', UK: 'UK', CA: 'CA', DE: 'DE', FR: 'FR', ES: 'ES', IT: 'IT', MX: 'MX', BR: 'BR', IN: 'IN', NL: 'NL' }
      return map[m] || m
    },
    resolvedProductId: function () {
      if (this.productId != null && this.productId !== '') return String(this.productId)
      return this.item ? listingIdFromProduct(this.item) : ''
    }
  },
  watch: {
    item: {
      handler: function () {
        this.loadFromItemFallback()
      },
      immediate: true
    },
    productId: function () {
      this.loadFromItemFallback()
      this.hydrateFromStockApiSilent()
    }
  },
  mounted: function () {
    this.hydrateFromStockApiSilent()
  },
  methods: {
    httpParams: function () {
      var p = {}
      if (this.sellerAccountId) p.seller_account_id = this.sellerAccountId
      return p
    },
    inputFilledClass: function (v) {
      return (v !== null && v !== undefined && v !== '' && !isNaN(Number(v))) ? 'has-value' : ''
    },
    /**
     * Immediate fill from watchdog row. Backend often merges account defaults here (e.g. warning 7d / critical 3d / 7d lookback)
     * for alert copy — those are not necessarily the same as per-listing overrides in stock-alert-thresholds.
     */
    loadFromItemFallback: function () {
      var s = this.item && this.item.settings ? this.item.settings : {}
      function num (v) {
        var n = unwrapApiNumber(v)
        if (n === null) return null
        return n >= 0 ? n : null
      }
      function numPos (v) {
        var n = unwrapApiNumber(v)
        if (n === null) return null
        return n > 0 ? n : null
      }
      function val (a, b) {
        return (a !== undefined && a !== null) ? a : b
      }
      this.form = {
        amberThreshold: num(val(s.amberThreshold, s.amber_threshold)),
        redThreshold: num(val(s.redThreshold, s.red_threshold)),
        amberDays: num(val(s.amberDays, s.amber_days)),
        redDays: num(val(s.redDays, s.red_days)),
        velocityLookbackDays: numPos(val(s.velocityLookbackDays, s.velocity_lookback_days))
      }
    },
    /** No loading UI: replace form when stock-alert-thresholds returns this listing (matches Stock page). */
    hydrateFromStockApiSilent: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      var self = this
      var base = this.stockThresholdsApiBase
      var opts = function (extra) {
        return {
          params: Object.assign({}, self.httpParams(), {
            channel: 'fba',
            product_ids: pid,
            _skcb: Date.now()
          }, extra || {}),
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          timeout: 45000
        }
      }
      var apply = function (res) {
        var items = extractThresholdGetItems(httpResponseEntity(res))
        var i
        for (i = 0; i < items.length; i++) {
          var row = items[i]
          if (productIdFromThresholdRow(row) !== pid) continue
          var raw = row.settings
          if (raw == null) raw = {}
          else if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw)
            } catch (e) {
              raw = {}
            }
          }
          var settings = typeof raw === 'object' && raw !== null ? raw : {}
          self.form = formFieldsFromFbaSettings(settings)
          return
        }
      }
      this.$http.get(base, opts()).then(function (res) {
        apply(res)
      }).catch(function (res) {
        if (res && res.status === 304) {
          return self.$http.get(base, opts({ _skretry: Date.now() })).then(function (res2) {
            apply(res2)
          })
        }
      })
    },
    toPayload: function () {
      var toVal = function (v) {
        if (v === null || v === '' || v === undefined) return null
        var n = Number(v)
        return isNaN(n) ? null : n
      }
      var vel = this.form.velocityLookbackDays
      var velocityLookbackDays = (vel !== null && vel !== '' && !isNaN(Number(vel))) ? Number(vel) : null
      return {
        amber_threshold: toVal(this.form.amberThreshold),
        red_threshold: toVal(this.form.redThreshold),
        amber_days: toVal(this.form.amberDays),
        red_days: toVal(this.form.redDays),
        velocity_lookback_days: velocityLookbackDays
      }
    },
    applySavedPayloadToItem: function (settings) {
      var item = this.item
      if (!item) return
      if (!item.settings) this.$set(item, 'settings', {})
      var s = settings
      if (Object.prototype.hasOwnProperty.call(s, 'amber_threshold')) {
        this.$set(item.settings, 'amber_threshold', s.amber_threshold)
        this.$set(item.settings, 'amberThreshold', s.amber_threshold)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'red_threshold')) {
        this.$set(item.settings, 'red_threshold', s.red_threshold)
        this.$set(item.settings, 'redThreshold', s.red_threshold)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'amber_days')) {
        this.$set(item.settings, 'amber_days', s.amber_days)
        this.$set(item.settings, 'amberDays', s.amber_days)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'red_days')) {
        this.$set(item.settings, 'red_days', s.red_days)
        this.$set(item.settings, 'redDays', s.red_days)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'velocity_lookback_days')) {
        this.$set(item.settings, 'velocity_lookback_days', s.velocity_lookback_days)
        this.$set(item.settings, 'velocityLookbackDays', s.velocity_lookback_days)
      }
    },
    save: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      this.saving = true
      this.saveError = null
      var settings = this.toPayload()
      var body = {
        channel: 'fba',
        updates: [{ product_id: pid, settings: settings }]
      }
      var self = this
      this.$http.patch(this.stockThresholdsApiBase, body, { params: this.httpParams(), timeout: 60000 }).then(function (res) {
        self.saving = false
        var failed = res.body && res.body.failed
        if (failed > 0) {
          self.saveError = 'Some updates failed. Check results from the server.'
          return
        }
        self.applySavedPayloadToItem(settings)
        self.$emit('saved')
      }, function (err) {
        self.saving = false
        self.saveError = (err.body && err.body.message) || err.statusText || 'Failed to save settings'
      })
    },
    cancel: function () {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.fba-thresholds-inline {
  padding: 16px 20px;
  border-top: 1px solid var(--border-light);
  background: #f8faff;
}

.fba-inline-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-navy-dark, #143a66);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.fba-inline-title .icon {
  margin: 0;
}

.fba-inline-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.fba-inline-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fba-inline-field label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.fba-inline-input {
  width: 88px;
  padding: 6px 8px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-white);
}

.fba-inline-input:focus {
  outline: none;
  border-color: var(--primary-navy-dark, #143a66);
  box-shadow: 0 0 0 2px rgba(27, 75, 130, 0.1);
}

.fba-inline-input.has-value {
  border-color: var(--primary-navy-light, #2a5f9e);
  background: #eff4fb;
  font-weight: 600;
}

.fba-inline-input::placeholder {
  color: var(--text-tertiary);
}

.fba-field-hint {
  font-size: 10px;
  color: var(--text-tertiary);
  text-align: center;
  margin-top: 2px;
}

.fba-inline-divider {
  width: 1px;
  height: 48px;
  background: var(--border-light);
  align-self: center;
}

.fba-inline-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fba-btn-save {
  padding: 8px 20px;
  background: var(--accent-orange);
  border: none;
  border-radius: 7px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.fba-btn-save:hover:not(:disabled) {
  background: var(--accent-orange-hover);
}

.fba-btn-save:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.fba-btn-cancel {
  padding: 8px 16px;
  background: none;
  border: 1px solid var(--border-medium);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  color: var(--text-secondary);
}

.fba-btn-cancel:hover {
  border-color: var(--text-secondary);
  color: var(--text-primary);
}

.fba-inline-hint {
  font-size: 11px;
  color: var(--text-tertiary);
}

.alert-thresholds-error {
  margin-top: 12px;
  color: var(--status-critical);
  font-size: 13px;
}

.alert-thresholds-loading {
  margin-top: 8px;
  margin-bottom: 4px;
  color: var(--text-secondary);
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// AlertThresholdsInline.vue?4768a9e4